/**
 * Shared chat suggested-action model for clarifications and follow-ups.
 *
 * Preferred structured field on assistant messages / `/ask` done chunks:
 *   suggested_actions: [{ id, label, sendText, kind }]
 *
 * Text markers (stripped from display):
 *   [SUGGESTED_ACTION:Label|send text]
 *   [SHOW_MORE_OPTIONS:Show more pork recipes]  // alias
 *   [SUGGESTED_ACTIONS]
 *   Label|send text
 *   [/SUGGESTED_ACTIONS]
 */

export const DEFAULT_SHOW_MORE_LABEL = 'See 3 more options'

const SINGLE_ACTION_RE = /\[SUGGESTED_ACTION:\s*([^|\]]+)\|([^\]]+)\]/gi
const SERVING_CHOICE_RE = /\[SERVING_CHOICE:\s*([^|\]]+)\|([^|\]]+)\|(add|use)\s*\]/gi
const DECISION_RUNG_RE = /\[DECISION_RUNG:\s*(?:recipe_text|household|pantry|convention)\s*\]/gi
const SHOW_MORE_RE = /\[SHOW_MORE_OPTIONS:\s*([^\]]+)\]/gi
const BLOCK_RE = /\[SUGGESTED_ACTIONS\]([\s\S]*?)\[\/SUGGESTED_ACTIONS\]/gi
const INCOMPLETE_MARKERS_RE =
  /\[(?:SUGGESTED_ACTION|SHOW_MORE_OPTIONS|SUGGESTED_ACTIONS|SERVING_CHOICE|DECISION_RUNG)[^\]]*$/i
const DID_YOU_MEAN_RE = /Did you mean\s+['"](.+?)['"]\s*\??/i
const FOUND_IS_THIS_RE =
  /I found\s+['"](.+?)['"]\.\s*Is this the recipe you're looking for\?/i
const CAPTAIN_COOK_RE = /Captain Cook app or the historical figure/i
const TIME_QUESTION_RE = /How much time do you have/i
const SAVED_SCOPE_QUESTION_RE = /only from recipes you've saved|saved in your account/i
const RECIPE_HEADING_RE = /^\s*#(\d+)\.\s*(?:\*\*)?(.+?)(?:\*\*)?\s*$/gm
const SAY_SHOW_MORE_QUOTED_RE =
  /Say\s+\*{0,2}['"]((?:Show|See)\s+more\s+[^'"]+)['"]\*{0,2}(?:\s+to see the next\s+\d+)?\.?/i
const SAY_SHOW_MORE_BOLD_RE =
  /Say\s+\*\*((?:Show|See)\s+more\s+[^*]+?)\*\*(?:\s+to see the next\s+\d+)?\.?/i
const INVITE_LINE_RE =
  /\n?(?:I found more(?: matching recipes)?\.\s*)?Say\s+\*{0,2}['"]?(?:Show|See)\s+more\s+[^.\n]+?(?:['"]\*{0,2}|\*{0,2})\s+to see the next\s+\d+\.?/gi

/**
 * @typedef {{ id: string, label: string, sendText: string, kind: string }} SuggestedAction
 */

export function actionsForDecideWhatToCook() {
  return normalizeSuggestedActions([
    { id: 'who-me', label: 'Just me', sendText: 'Just me', kind: 'clarification' },
    { id: 'who-family', label: 'Family', sendText: 'Family', kind: 'clarification' },
    { id: 'who-guests', label: 'Guests', sendText: 'Guests', kind: 'clarification' },
    {
      id: 'time-quick',
      label: 'Quick (<30 min)',
      sendText: 'Quick under 30 minutes',
      kind: 'clarification',
    },
    {
      id: 'time-medium',
      label: 'Medium (30–60)',
      sendText: 'Medium 30 to 60 minutes',
      kind: 'clarification',
    },
    {
      id: 'time-long',
      label: 'No rush',
      sendText: 'No rush on time',
      kind: 'clarification',
    },
    {
      id: 'scope-saved',
      label: 'Saved only',
      sendText: 'saved recipes only',
      kind: 'clarification',
    },
    {
      id: 'scope-kb',
      label: 'Include uploads/KB',
      sendText: 'include uploaded recipes too',
      kind: 'clarification',
    },
    {
      id: 'scope-both',
      label: 'Both',
      sendText: 'both saved and uploaded recipes',
      kind: 'clarification',
    },
  ])
}

export function actionsForCaptainCookDisambiguation() {
  return normalizeSuggestedActions([
    {
      id: 'scope-app',
      label: 'Captain Cook app',
      sendText: 'Captain Cook app',
      kind: 'clarification',
    },
    {
      id: 'scope-history',
      label: 'Historical figure',
      sendText: 'Historical figure',
      kind: 'clarification',
    },
  ])
}

export function actionsFromRecipeSummaryReply(text) {
  const raw = String(text || '')
  const titles = []
  RECIPE_HEADING_RE.lastIndex = 0
  let match
  while ((match = RECIPE_HEADING_RE.exec(raw)) !== null) {
    let title = match[2].trim().replace(/^\*+|\*+$/g, '').trim()
    title = title.replace(/\s*\([^)]*\)\s*$/, '').trim()
    if (title) titles.push(title)
  }
  if (!titles.length) return []

  const actions = titles.slice(0, 3).map((name, idx) => {
    const n = idx + 1
    return {
      id: `numbered-${n}`,
      label: name.length <= 40 ? `#${n} ${name}` : `#${n} details`,
      sendText: `details for #${n}`,
      kind: 'numbered_pick',
    }
  })
  actions.push(
    {
      id: 'followup-details',
      label: 'Recipe details',
      sendText: 'Recipe Details',
      kind: 'follow_up',
    },
    {
      id: 'followup-explore',
      label: 'Explore more options',
      sendText: 'Explore more options',
      kind: 'follow_up',
    }
  )
  return normalizeSuggestedActions(actions)
}

/**
 * @param {unknown} raw
 * @param {{ defaultKind?: string, index?: number }} [opts]
 * @returns {SuggestedAction|null}
 */
export function normalizeSuggestedAction(raw, opts = {}) {
  if (!raw || typeof raw !== 'object') return null
  const label = String(raw.label || '').trim()
  const sendText = String(raw.sendText || raw.send_text || raw.sendPhrase || '').trim()
  if (!label || !sendText) return null
  const kind = String(raw.kind || opts.defaultKind || 'follow_up').trim() || 'follow_up'
  const index = opts.index ?? 0
  const id = String(raw.id || `${kind}-${index}-${label.slice(0, 32)}`).trim()
  const action = { id, label, sendText, kind }
  const servingItem = String(raw.servingItem || raw.serving_item || '').trim()
  const servingAction = String(raw.servingAction || raw.serving_action || '').trim().toLowerCase()
  if (servingItem) action.servingItem = servingItem.slice(0, 80)
  if (servingAction === 'add' || servingAction === 'use') action.servingAction = servingAction
  return action
}

/**
 * @param {unknown} rawList
 * @returns {SuggestedAction[]}
 */
export function normalizeSuggestedActions(rawList) {
  if (!Array.isArray(rawList)) return []
  const out = []
  const seen = new Set()
  rawList.forEach((item, index) => {
    const action = normalizeSuggestedAction(item, { index })
    if (!action) return
    const key = action.sendText.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(action)
  })
  return out
}

/**
 * @param {unknown} questions
 * @returns {SuggestedAction[]}
 */
export function actionsFromClarificationQuestions(questions) {
  if (!Array.isArray(questions) || questions.length === 0) return []
  const joined = questions.map((q) => String(q || '')).join('\n')
  if (CAPTAIN_COOK_RE.test(joined)) return actionsForCaptainCookDisambiguation()
  if (TIME_QUESTION_RE.test(joined)) return actionsForDecideWhatToCook()
  if (SAVED_SCOPE_QUESTION_RE.test(joined)) {
    return normalizeSuggestedActions([
      {
        id: 'scope-saved',
        label: 'Saved only',
        sendText: 'saved recipes only',
        kind: 'clarification',
      },
      {
        id: 'scope-kb',
        label: 'Include uploads/KB',
        sendText: 'include uploaded recipes too',
        kind: 'clarification',
      },
      {
        id: 'scope-both',
        label: 'Both',
        sendText: 'both saved and uploaded recipes',
        kind: 'clarification',
      },
    ])
  }

  const actions = []
  questions.forEach((q, i) => {
    const text = String(q || '').trim()
    if (!text) return
    const mean = text.match(DID_YOU_MEAN_RE)
    const found = text.match(FOUND_IS_THIS_RE)
    const confirmedName = mean?.[1] || found?.[1]
    if (confirmedName) {
      const name = confirmedName.trim()
      actions.push(
        {
          id: `clarification-yes-${i}`,
          label: `Yes — ${name}`,
          sendText: `Yes, I meant ${name}`,
          kind: 'clarification',
        },
        {
          id: `clarification-no-${i}`,
          label: 'No',
          sendText: 'No',
          kind: 'yes_no',
        }
      )
      return
    }
    const option = text.replace(/^\d+[.)]\s*/, '').trim()
    if (option.includes('?')) return
    if (option.length > 1 && option.length <= 80) {
      actions.push({
        id: `clarification-opt-${i}`,
        label: option,
        sendText: option,
        kind: 'clarification',
      })
    }
  })
  return normalizeSuggestedActions(actions)
}

/**
 * @param {unknown} text
 * @returns {{ cleanedText: string, actions: SuggestedAction[] }}
 */
export function extractSuggestedActionsFromText(text) {
  const raw = String(text ?? '')
  if (!raw) return { cleanedText: '', actions: [] }

  /** @type {SuggestedAction[]} */
  const actions = []
  const add = (label, sendText, kind) => {
    actions.push({
      id: `${kind}-${actions.length}`,
      label: String(label).trim(),
      sendText: String(sendText).trim(),
      kind,
    })
  }

  let cleaned = raw

  DECISION_RUNG_RE.lastIndex = 0
  cleaned = cleaned.replace(DECISION_RUNG_RE, '')

  SERVING_CHOICE_RE.lastIndex = 0
  cleaned = cleaned.replace(SERVING_CHOICE_RE, (_full, label, item, servingAction) => {
    const buttonLabel = String(label || '').trim()
    const servingItem = String(item || '').trim()
    const action = String(servingAction || '').trim().toLowerCase()
    if (buttonLabel && servingItem && (action === 'add' || action === 'use')) {
      actions.push({
        id: `serving_choice-${actions.length}`,
        label: buttonLabel,
        sendText: buttonLabel,
        kind: 'serving_choice',
        servingItem,
        servingAction: action,
      })
    }
    return ''
  })

  cleaned = cleaned.replace(BLOCK_RE, (_full, body) => {
    String(body || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const pipe = line.indexOf('|')
        if (pipe < 1) return
        const label = line.slice(0, pipe).trim()
        const sendText = line.slice(pipe + 1).trim()
        if (label && sendText) add(label, sendText, 'follow_up')
      })
    return ''
  })

  cleaned = cleaned.replace(SINGLE_ACTION_RE, (_full, label, sendText) => {
    add(label, sendText, 'follow_up')
    return ''
  })

  cleaned = cleaned.replace(SHOW_MORE_RE, (_full, phrase) => {
    add(DEFAULT_SHOW_MORE_LABEL, phrase, 'show_more')
    return ''
  })

  // NL fallback for older show-more invites
  if (!actions.some((a) => a.kind === 'show_more')) {
    const quoted = cleaned.match(SAY_SHOW_MORE_QUOTED_RE)
    const bold = quoted ? null : cleaned.match(SAY_SHOW_MORE_BOLD_RE)
    const phrase = (quoted?.[1] || bold?.[1] || '').trim().replace(/\*+$/, '').trim()
    if (phrase) {
      add(DEFAULT_SHOW_MORE_LABEL, phrase, 'show_more')
      cleaned = cleaned.replace(INVITE_LINE_RE, '')
    }
  } else {
    cleaned = cleaned.replace(INVITE_LINE_RE, '')
  }

  cleaned = cleaned
    .replace(INCOMPLETE_MARKERS_RE, '')
    .replace(/[^\S\n\r]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  return {
    cleanedText: cleaned || raw.trim(),
    actions: normalizeSuggestedActions(actions),
  }
}

/**
 * Resolve actions for an assistant chat message.
 * Prefer API `suggested_actions`; fall back to text markers / clarification questions.
 *
 * @param {{ text?: string, suggested_actions?: unknown, clarification_questions?: unknown }} message
 * @returns {{ cleanedText: string, actions: SuggestedAction[] }}
 */
export function resolveSuggestedActions(message) {
  const text = String(message?.text ?? '')
  const fromApi = normalizeSuggestedActions(message?.suggested_actions)
  const fromText = extractSuggestedActionsFromText(text)
  const fromClarification =
    fromApi.length || fromText.actions.length
      ? []
      : actionsFromClarificationQuestions(message?.clarification_questions)

  const fromSummary =
    fromApi.length || fromText.actions.length || fromClarification.length
      ? []
      : actionsFromRecipeSummaryReply(fromText.cleanedText || text)

  const actions = fromApi.length
    ? fromApi
    : fromText.actions.length
      ? fromText.actions
      : fromClarification.length
        ? fromClarification
        : fromSummary

  return {
    cleanedText: fromApi.length ? fromText.cleanedText || text : fromText.cleanedText,
    actions,
  }
}

/** @deprecated Prefer resolveSuggestedActions / extractSuggestedActionsFromText */
export function extractShowMoreAction(text) {
  const { cleanedText, actions } = extractSuggestedActionsFromText(text)
  const showMore = actions.find((a) => a.kind === 'show_more') || null
  return {
    cleanedText,
    action: showMore
      ? { sendPhrase: showMore.sendText, label: showMore.label }
      : null,
  }
}
